import { ValidatorFn } from "@angular/forms";
import { ColumnDef } from "./column-definition";
import { Validators } from '@angular/forms';
import { TransactionInterface } from "./transaction-interface";

/** Validators for fields */
const validators = new Map<string, ValidatorFn[]>([
    ["date", [
        Validators.required,
    ]],
    ["type", [
        Validators.required,
    ]],
    ["description", [
        Validators.required,
    ]],
    ["invoiceNumber", [
        Validators.required,
    ]],
    ["totalAmount", [
        Validators.required,
    ]],
]);

/**
 * Represents a transaction model, which wraps the transaction interface
 * with added functionality.
 */
export class Transaction {
    /** Attribute fields */
    private _date: string;
    private _type: string;
    private _description: string;
    private _invoiceNumber: string;
    private _totalAmount: number;

    /** Validators for transaction forms */
    public static validators = validators;

    /**
     * Construct the transaction from an interface
     */
    constructor(intf: TransactionInterface) {
        this._date = intf.TXN_DATE?.trim() || "";
        this._type = intf.TXN_TYPE?.trim() || "";
        this._description = intf.TXN_DESCRIPTION?.trim() || "";
        this._invoiceNumber = intf.INVOICE_NUMBER?.trim() || "";
        this._totalAmount = parseFloat(intf.TOTAL_AMOUNT) + 0;
    }

    /**
     * Determine if two transactions are equal
     * 
     * @param txn The other transactions to check
     * @returns True if transactions are equal
     */
    public equals(txn: Transaction): boolean {
        return txn.date === this.date &&
            txn.type === this.type &&
            txn.description === this.description &&
            txn.invoiceNumber === this.invoiceNumber &&
            txn.totalAmount === this.totalAmount;
    }

    /**
     * Return a copy of this transaction
     */
    public copyOf(): Transaction {
        return new Transaction(this.asInterface());
    }

    /**
     * Return the transaction as an interface
     */
    public asInterface(): TransactionInterface {
        const intf: TransactionInterface = {
            TXN_DATE: this.date,
            TXN_TYPE: this.type,
            TXN_DESCRIPTION: this.description,
            INVOICE_NUMBER: this.invoiceNumber,
            TOTAL_AMOUNT: ""+this.totalAmount,
        };

        return intf;
    }

    /**
     * Return the transaction as a string representation, used for filtering/searching
     * 
     * We use a unicode full block as a delimeter, so the only string matches that
     * return are from our fields (unless the user is typing the unicode full block
     * character like a maniac)
     */
    public toString(defs: ColumnDef[]): string {
        let str = "";
        for (let col of defs) {
            if (col.toStringTransform != undefined) {
                str += col.toStringTransform((this as any)[col.field]) + "█";
            } else {
                str += (this as any)[col.field];
            }
        }

        return str.toLowerCase();
    }

    /** Setters */
    set date(date: string) {
        this._date = date;
    }

    set type(type: string) {
        this._type = type;
    }

    set description(description: string) {
        this._description = description;
    }

    set invoiceNumber(invoiceNumber: string) {
        this._invoiceNumber = invoiceNumber;
    }

    set totalAmount(totalAmount: number) {
        this._totalAmount = totalAmount;
    }

    /** Getters */
    get date(): string {
        return this._date;
    }

    get type(): string {
        return this._type;
    }

    get description(): string {
        return this._description;
    }

    get invoiceNumber(): string {
        return this._invoiceNumber;
    }

    get totalAmount(): number {
        return this._totalAmount;
    }
}